import React, { useState, useEffect } from 'react';
import { 
  Lock, LogOut, User, Briefcase, GraduationCap, Image, 
  Award, Save, Plus, Trash2, Edit, X, Eye, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import '../styles/light-theme.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Auth Context
const useAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('admin_token');
        setToken(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token } = response.data;
    localStorage.setItem('admin_token', access_token);
    setToken(access_token);
    setIsAuthenticated(true);
    return true;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  return { token, isAuthenticated, loading, login, logout };
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '16px',
        padding: '32px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: 'var(--shadow-xl)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</h2>
          <button onClick={onClose} style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
            <X size={24} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Form Input Component
const FormInput = ({ label, type = 'text', value, onChange, placeholder, required = false, textarea = false }) => {
  const InputElement = textarea ? 'textarea' : 'input';
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '8px' }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      <InputElement
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={textarea ? 4 : undefined}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          fontSize: '0.9375rem',
          outline: 'none',
          resize: textarea ? 'vertical' : 'none',
          fontFamily: 'inherit'
        }}
      />
    </div>
  );
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onLogin(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'var(--bg-secondary)'
    }}>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '20px',
        padding: '48px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: 'var(--shadow-xl)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, var(--accent-blue) 0%, var(--accent-purple) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Lock size={24} color="white" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Admin Panel
          </h1>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
            Sign in to manage your portfolio
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.8125rem', 
              fontWeight: 500, 
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                fontSize: '0.9375rem',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              placeholder="admin"
              data-testid="admin-username"
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '0.8125rem', 
              fontWeight: 500, 
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                fontSize: '0.9375rem',
                outline: 'none'
              }}
              placeholder="••••••••"
              data-testid="admin-password"
            />
          </div>

          {error && (
            <div style={{
              padding: '12px',
              background: '#fee2e2',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '0.8125rem',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{ width: '100%' }}
            data-testid="admin-login-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeSection, setActiveSection, onLogout }) => {
  const sections = [
    { id: 'projects', label: 'UX Projects', icon: Briefcase },
    { id: 'photos', label: 'Photography', icon: Image },
    { id: 'experience', label: 'Experience', icon: User },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'certifications', label: 'Certifications', icon: Award },
  ];

  return (
    <aside style={{
      width: '260px',
      background: 'var(--bg-card)',
      borderRight: '1px solid var(--border-color)',
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      padding: '24px 16px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ marginBottom: '32px', padding: '0 12px' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Portfolio CMS
        </h2>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          Content Management
        </p>
      </div>

      <nav style={{ flex: 1 }}>
        {sections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                marginBottom: '4px',
                background: isActive ? 'var(--bg-secondary)' : 'transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: isActive ? 500 : 400,
                fontSize: '0.875rem',
                transition: 'all 0.2s ease'
              }}
              data-testid={`nav-${section.id}`}
            >
              <Icon size={18} />
              {section.label}
              {isActive && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
            </button>
          );
        })}
      </nav>

      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            borderRadius: '10px',
            fontSize: '0.875rem',
            marginBottom: '4px'
          }}
        >
          <Eye size={18} />
          View Portfolio
        </a>
        <button
          onClick={onLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            background: 'transparent',
            color: '#dc2626',
            fontSize: '0.875rem'
          }}
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

// Generic Data Table
const DataTable = ({ items, columns, onEdit, onDelete, emptyMessage }) => {
  if (items.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px',
        color: 'var(--text-tertiary)'
      }}>
        {emptyMessage || 'No items found'}
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-card)',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bg-secondary)' }}>
            {columns.map((col, idx) => (
              <th key={idx} style={{
                padding: '14px 16px',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                {col.label}
              </th>
            ))}
            <th style={{ width: '100px' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.id || idx} style={{ borderTop: '1px solid var(--border-color)' }}>
              {columns.map((col, colIdx) => (
                <td key={colIdx} style={{
                  padding: '14px 16px',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)'
                }}>
                  {col.render ? col.render(item) : item[col.key]}
                </td>
              ))}
              <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                <button
                  onClick={() => onEdit(item)}
                  style={{
                    padding: '6px',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: 'var(--accent-blue)',
                    marginRight: '8px'
                  }}
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  style={{
                    padding: '6px',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: '#dc2626'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Projects Section
const ProjectsSection = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '', subtitle: '', category: '', slug: '', thumbnail: '',
    description: '', problem: '', solution: '', outcome: '', isFeatured: false
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingProject(null);
    setFormData({
      title: '', subtitle: '', category: '', slug: '', thumbnail: '',
      description: '', problem: '', solution: '', outcome: '', isFeatured: false
    });
    setModalOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      subtitle: project.subtitle || '',
      category: project.category || '',
      slug: project.slug || '',
      thumbnail: project.thumbnail || '',
      description: project.description || '',
      problem: project.problem || '',
      solution: project.solution || '',
      outcome: project.outcome || '',
      isFeatured: project.is_featured || false
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        subtitle: formData.subtitle,
        category: formData.category,
        slug: formData.slug,
        thumbnail: formData.thumbnail,
        description: formData.description,
        problem: formData.problem,
        solution: formData.solution,
        outcome: formData.outcome,
        is_featured: formData.isFeatured,
        metrics: [],
        sections: [],
        is_active: true
      };

      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/projects`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setModalOpen(false);
      fetchProjects();
    } catch (err) {
      alert('Error saving project: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(projects.filter(p => p.id !== id));
    } catch (err) {
      alert('Error deleting project');
    }
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'slug', label: 'Slug' },
    { 
      key: 'is_featured', 
      label: 'Featured',
      render: (item) => item.is_featured ? '⭐' : '-'
    }
  ];

  if (loading) return <div className="loading-spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            UX Projects
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
            Manage your case study projects
          </p>
        </div>
        <button onClick={handleAdd} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Plus size={18} /> Add Project
        </button>
      </div>
      
      <DataTable
        items={projects}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="No projects yet"
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingProject ? 'Edit Project' : 'Add Project'}>
        <form onSubmit={handleSubmit}>
          <FormInput label="Title" value={formData.title} onChange={(v) => setFormData({...formData, title: v})} required />
          <FormInput label="Subtitle" value={formData.subtitle} onChange={(v) => setFormData({...formData, subtitle: v})} />
          <FormInput label="Category" value={formData.category} onChange={(v) => setFormData({...formData, category: v})} required />
          <FormInput label="Slug" value={formData.slug} onChange={(v) => setFormData({...formData, slug: v})} required />
          <FormInput label="Thumbnail URL" value={formData.thumbnail} onChange={(v) => setFormData({...formData, thumbnail: v})} />
          <FormInput label="Description" value={formData.description} onChange={(v) => setFormData({...formData, description: v})} textarea />
          <FormInput label="Problem" value={formData.problem} onChange={(v) => setFormData({...formData, problem: v})} textarea />
          <FormInput label="Solution" value={formData.solution} onChange={(v) => setFormData({...formData, solution: v})} textarea />
          <FormInput label="Outcome" value={formData.outcome} onChange={(v) => setFormData({...formData, outcome: v})} textarea />
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isFeatured}
                onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
              />
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Featured Project</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'transparent', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingProject ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Photos Section  
const PhotosSection = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [formData, setFormData] = useState({
    title: '', src: '', category: '', location: '', story: '', order: 0
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos`);
      setPhotos(response.data);
    } catch (err) {
      console.error('Error fetching photos:', err);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingPhoto(null);
    setFormData({ title: '', src: '', category: '', location: '', story: '', order: photos.length });
    setModalOpen(true);
  };

  const handleEdit = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      title: photo.title || '',
      src: photo.src || '',
      category: photo.category || '',
      location: photo.location || '',
      story: photo.story || '',
      order: photo.order || 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, order: parseInt(formData.order) };

      if (editingPhoto) {
        await axios.put(`${API}/photos/${editingPhoto.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/photos`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setModalOpen(false);
      fetchPhotos();
    } catch (err) {
      alert('Error saving photo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/photos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(photos.filter(p => p.id !== id));
    } catch (err) {
      alert('Error deleting photo');
    }
  };

  const columns = [
    { 
      key: 'src', 
      label: 'Preview',
      render: (item) => (
        <img src={item.src} alt={item.title} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} />
      )
    },
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'location', label: 'Location' }
  ];

  if (loading) return <div className="loading-spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Photography
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
            Manage your photo gallery
          </p>
        </div>
        <button onClick={handleAdd} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Plus size={18} /> Add Photo
        </button>
      </div>
      
      <DataTable
        items={photos}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="No photos yet"
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingPhoto ? 'Edit Photo' : 'Add Photo'}>
        <form onSubmit={handleSubmit}>
          <FormInput label="Title" value={formData.title} onChange={(v) => setFormData({...formData, title: v})} required />
          <FormInput label="Image URL" value={formData.src} onChange={(v) => setFormData({...formData, src: v})} required />
          <FormInput label="Category" value={formData.category} onChange={(v) => setFormData({...formData, category: v})} />
          <FormInput label="Location" value={formData.location} onChange={(v) => setFormData({...formData, location: v})} />
          <FormInput label="Story" value={formData.story} onChange={(v) => setFormData({...formData, story: v})} textarea />
          <FormInput label="Order" type="number" value={formData.order} onChange={(v) => setFormData({...formData, order: v})} />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'transparent', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingPhoto ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Experience Section
const ExperienceSection = ({ token }) => {
  const [experience, setExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [formData, setFormData] = useState({
    role: '', company: '', period: '', location: '', description: '', order: 0
  });

  useEffect(() => {
    fetchExperience();
  }, []);

  const fetchExperience = async () => {
    try {
      const response = await axios.get(`${API}/experience`);
      setExperience(response.data);
    } catch (err) {
      console.error('Error fetching experience:', err);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingExp(null);
    setFormData({ role: '', company: '', period: '', location: '', description: '', order: experience.length });
    setModalOpen(true);
  };

  const handleEdit = (exp) => {
    setEditingExp(exp);
    setFormData({
      role: exp.role || '',
      company: exp.company || '',
      period: exp.period || '',
      location: exp.location || '',
      description: exp.description || '',
      order: exp.order || 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, order: parseInt(formData.order) };

      if (editingExp) {
        await axios.put(`${API}/experience/${editingExp.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/experience`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setModalOpen(false);
      fetchExperience();
    } catch (err) {
      alert('Error saving experience: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this experience?')) return;
    try {
      await axios.delete(`${API}/experience/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExperience(experience.filter(e => e.id !== id));
    } catch (err) {
      alert('Error deleting experience');
    }
  };

  const columns = [
    { key: 'role', label: 'Role' },
    { key: 'company', label: 'Company' },
    { key: 'period', label: 'Period' },
    { key: 'location', label: 'Location' }
  ];

  if (loading) return <div className="loading-spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Experience
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
            Manage your career timeline
          </p>
        </div>
        <button onClick={handleAdd} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Plus size={18} /> Add Experience
        </button>
      </div>
      
      <DataTable
        items={experience}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="No experience entries yet"
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingExp ? 'Edit Experience' : 'Add Experience'}>
        <form onSubmit={handleSubmit}>
          <FormInput label="Role" value={formData.role} onChange={(v) => setFormData({...formData, role: v})} required />
          <FormInput label="Company" value={formData.company} onChange={(v) => setFormData({...formData, company: v})} required />
          <FormInput label="Period" value={formData.period} onChange={(v) => setFormData({...formData, period: v})} placeholder="e.g., Jan 2020 - Present" required />
          <FormInput label="Location" value={formData.location} onChange={(v) => setFormData({...formData, location: v})} />
          <FormInput label="Description" value={formData.description} onChange={(v) => setFormData({...formData, description: v})} textarea />
          <FormInput label="Order" type="number" value={formData.order} onChange={(v) => setFormData({...formData, order: v})} />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'transparent', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingExp ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Education Section
const EducationSection = ({ token }) => {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEdu, setEditingEdu] = useState(null);
  const [formData, setFormData] = useState({
    degree: '', institution: '', year: '', location: '', description: '', order: 0
  });

  useEffect(() => {
    fetchEducation();
  }, []);

  const fetchEducation = async () => {
    try {
      const response = await axios.get(`${API}/education`);
      setEducation(response.data);
    } catch (err) {
      console.error('Error fetching education:', err);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingEdu(null);
    setFormData({ degree: '', institution: '', year: '', location: '', description: '', order: education.length });
    setModalOpen(true);
  };

  const handleEdit = (edu) => {
    setEditingEdu(edu);
    setFormData({
      degree: edu.degree || '',
      institution: edu.institution || '',
      year: edu.year || '',
      location: edu.location || '',
      description: edu.description || '',
      order: edu.order || 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, order: parseInt(formData.order) };

      if (editingEdu) {
        await axios.put(`${API}/education/${editingEdu.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/education`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setModalOpen(false);
      fetchEducation();
    } catch (err) {
      alert('Error saving education: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this education entry?')) return;
    try {
      await axios.delete(`${API}/education/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEducation(education.filter(e => e.id !== id));
    } catch (err) {
      alert('Error deleting education');
    }
  };

  const columns = [
    { key: 'degree', label: 'Degree' },
    { key: 'institution', label: 'Institution' },
    { key: 'year', label: 'Year' },
    { key: 'location', label: 'Location' }
  ];

  if (loading) return <div className="loading-spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Education
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
            Manage your educational background
          </p>
        </div>
        <button onClick={handleAdd} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Plus size={18} /> Add Education
        </button>
      </div>
      
      <DataTable
        items={education}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="No education entries yet"
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingEdu ? 'Edit Education' : 'Add Education'}>
        <form onSubmit={handleSubmit}>
          <FormInput label="Degree" value={formData.degree} onChange={(v) => setFormData({...formData, degree: v})} required />
          <FormInput label="Institution" value={formData.institution} onChange={(v) => setFormData({...formData, institution: v})} required />
          <FormInput label="Year" value={formData.year} onChange={(v) => setFormData({...formData, year: v})} required />
          <FormInput label="Location" value={formData.location} onChange={(v) => setFormData({...formData, location: v})} />
          <FormInput label="Description" value={formData.description} onChange={(v) => setFormData({...formData, description: v})} textarea />
          <FormInput label="Order" type="number" value={formData.order} onChange={(v) => setFormData({...formData, order: v})} />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'transparent', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingEdu ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Certifications Section
const CertificationsSection = ({ token }) => {
  const [certifications, setCertifications] = useState([]);
  const [newCert, setNewCert] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCertifications = async () => {
      try {
        const response = await axios.get(`${API}/certifications`);
        setCertifications(response.data.certifications || []);
      } catch (err) {
        console.error('Error fetching certifications:', err);
      }
      setLoading(false);
    };
    fetchCertifications();
  }, []);

  const handleAdd = async () => {
    if (!newCert.trim()) return;
    const updated = [...certifications, newCert.trim()];
    setSaving(true);
    try {
      await axios.put(`${API}/certifications`, updated, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertifications(updated);
      setNewCert('');
    } catch (err) {
      alert('Error saving certifications');
    }
    setSaving(false);
  };

  const handleRemove = async (index) => {
    const updated = certifications.filter((_, i) => i !== index);
    setSaving(true);
    try {
      await axios.put(`${API}/certifications`, updated, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertifications(updated);
    } catch (err) {
      alert('Error saving certifications');
    }
    setSaving(false);
  };

  if (loading) return <div className="loading-spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Certifications
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
          Manage your professional certifications
        </p>
      </div>

      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <input
            type="text"
            value={newCert}
            onChange={(e) => setNewCert(e.target.value)}
            placeholder="Add new certification..."
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              fontSize: '0.9375rem',
              outline: 'none'
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          />
          <button
            onClick={handleAdd}
            disabled={saving}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={18} />
            Add
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {certifications.map((cert, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)'
            }}>
              <Award size={14} color="var(--accent-blue)" />
              {cert}
              <button
                onClick={() => handleRemove(idx)}
                style={{
                  padding: '2px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: 'transparent',
                  color: 'var(--text-tertiary)'
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Admin Panel
const AdminPanel = () => {
  const { token, isAuthenticated, loading, login, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('projects');

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)'
      }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={login} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'projects': return <ProjectsSection token={token} />;
      case 'photos': return <PhotosSection token={token} />;
      case 'experience': return <ExperienceSection token={token} />;
      case 'education': return <EducationSection token={token} />;
      case 'certifications': return <CertificationsSection token={token} />;
      default: return <ProjectsSection token={token} />;
    }
  };

  return (
    <div style={{ background: 'var(--bg-secondary)', minHeight: '100vh' }} data-testid="admin-panel">
      <Sidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        onLogout={logout}
      />
      <main style={{ marginLeft: '260px', padding: '32px 40px' }}>
        {renderSection()}
      </main>
    </div>
  );
};

export default AdminPanel;
